// Animation authoring → typed operations (CREATIVE-MODEL §7). The canonical
// animation is the list of keyframe tracks; marketer-friendly effects
// ("Entrada: deslizar desde la izquierda") are presets EXPANDED ONCE into
// keyframes by the Creative Engine (`expandPreset`), keeping `preset` only as
// provenance. There is no preset runtime and no second animation model.
//
// Format variants: tracks are presentation (LayoutOverride.animations REPLACES
// a variant's list). Editing in the element's home variant writes its base
// list; before that, every other variant that was still inheriting the base
// list gets its current list pinned as an explicit override, so editing the
// 300x250 never changes the 300x600 (same approach as layer order, 7.6C).
// The visibility window (`timing`) is NOT presentation: shared by all variants.

import {
  ANIMATION_PRESETS_V1,
  expandPreset,
  sampleKeyframes,
  type AnimatableProps,
  type AnimationPresetId,
  type AnimationTrack,
  type CreativeDocument,
  type CreativeOperation,
  type Easing,
} from "@interactads/creative-model";
import { editsBase, resolvedTracks } from "./commands";
import { findElement, layoutsOf, nextId, overrideOf } from "./model";

type Ops = CreativeOperation[];
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export const tracksOf = resolvedTracks;

/** Writes an element's complete track list in one variant (see the independence rule above). */
export function writeTracks(
  doc: CreativeDocument,
  layoutId: string,
  elementId: string,
  tracks: readonly AnimationTrack[]
): Ops {
  const el = findElement(doc, elementId);
  if (!el) return [];
  const next = clone([...tracks]);
  if (!editsBase(doc, layoutId, elementId)) {
    const current = overrideOf(doc, layoutId, elementId) ?? {};
    return [
      {
        op: "setLayoutOverride",
        layout: layoutId,
        element: elementId,
        override: { ...clone(current), animations: next },
      },
    ];
  }
  const ops: Ops = [];
  const inherited = clone(el.animations ?? []);
  for (const other of layoutsOf(doc, el)) {
    if (other === layoutId) continue;
    const override = overrideOf(doc, other, elementId);
    if (override?.animations !== undefined) continue;
    ops.push({
      op: "setLayoutOverride",
      layout: other,
      element: elementId,
      override: { ...clone(override ?? {}), animations: inherited },
    });
  }
  ops.push(
    next.length === 0
      ? { op: "updateElement", id: elementId, set: {}, unset: ["animations"] }
      : { op: "updateElement", id: elementId, set: { animations: next } }
  );
  return ops;
}

// ─── Entrance / exit effects ────────────────────────────────────────────────

export type EffectKind = "none" | "fade" | "slide" | "zoom";
/** Slide side: where the element comes FROM (entrance) or goes TO (exit). */
export type Side = "left" | "right" | "top" | "bottom";
export type EffectPhase = "entrance" | "exit";

export interface Effect {
  readonly kind: Exclude<EffectKind, "none">;
  readonly side?: Side;
  /** Scene time (ms) where the effect starts. */
  readonly at: number;
  readonly duration: number;
  /** Slide distance in px. */
  readonly distance?: number;
}

const ENTRANCE: Record<string, AnimationPresetId> = {
  fade: "fadeIn",
  zoom: "zoomIn",
  "slide:left": "slideRight",
  "slide:right": "slideLeft",
  "slide:top": "slideDown",
  "slide:bottom": "slideUp",
};
const EXIT: Record<string, AnimationPresetId> = {
  fade: "fadeOut",
  zoom: "zoomOut",
  "slide:left": "slideOutLeft",
  "slide:right": "slideOutRight",
  "slide:top": "slideOutUp",
  "slide:bottom": "slideOutDown",
};

export const DEFAULT_SLIDE_DISTANCE = 40;

export function presetFor(phase: EffectPhase, kind: Effect["kind"], side: Side = "left"): AnimationPresetId {
  const table = phase === "entrance" ? ENTRANCE : EXIT;
  return (kind === "slide" ? table[`slide:${side}`] : table[kind]) as AnimationPresetId;
}

/** Marketer label of a track: its preset, or "Personalizada" when its keyframes were edited by hand. */
export const PRESET_LABELS: Readonly<Record<AnimationPresetId, string>> = {
  fadeIn: "Aparecer",
  fadeOut: "Desvanecer",
  slideUp: "Deslizar desde abajo",
  slideDown: "Deslizar desde arriba",
  slideLeft: "Deslizar desde la derecha",
  slideRight: "Deslizar desde la izquierda",
  zoomIn: "Acercar (zoom in)",
  zoomOut: "Alejar (zoom out)",
  slideOutUp: "Salir hacia arriba",
  slideOutDown: "Salir hacia abajo",
  slideOutLeft: "Salir hacia la izquierda",
  slideOutRight: "Salir hacia la derecha",
  pulse: "Pulso",
  shake: "Sacudir",
  pop: "Saltito",
};

export const PHASE_LABELS: Readonly<Record<AnimationTrack["phase"], string>> = {
  entrance: "Entrada",
  exit: "Salida",
  emphasis: "Énfasis",
  custom: "Personalizada",
};

export function trackLabel(track: AnimationTrack): string {
  const preset = track.preset?.id as AnimationPresetId | undefined;
  return preset && Object.hasOwn(PRESET_LABELS, preset) ? PRESET_LABELS[preset] : PHASE_LABELS[track.phase];
}

/** The simple-effect view of a phase: the FIRST track of that phase. */
export function effectOf(
  tracks: readonly AnimationTrack[],
  phase: EffectPhase
): { readonly track: AnimationTrack; readonly kind: EffectKind | "custom"; readonly side?: Side } | null {
  const track = tracks.find((t) => t.phase === phase);
  if (!track) return null;
  const table = phase === "entrance" ? ENTRANCE : EXIT;
  const entry = Object.entries(table).find(([, preset]) => preset === track.preset?.id);
  if (!entry) return { track, kind: "custom" };
  const [kind, side] = entry[0].split(":") as [EffectKind, Side | undefined];
  return { track, kind, ...(side ? { side } : {}) };
}

/** Sets (or removes, with null) the entrance/exit effect: one preset expansion, in place of the previous one. */
export function withEffect(
  tracks: readonly AnimationTrack[],
  phase: EffectPhase,
  effect: Effect | null
): AnimationTrack[] {
  const index = tracks.findIndex((t) => t.phase === phase);
  const rest = tracks.filter((_, i) => i !== index);
  if (effect === null) return rest;
  const trackId =
    tracks[index]?.id ??
    nextId(
      tracks.map((t) => t.id),
      phase === "entrance" ? "in" : "out"
    );
  const preset = presetFor(phase, effect.kind, effect.side);
  const track = expandPreset({
    preset,
    trackId,
    at: Math.max(0, Math.round(effect.at)),
    duration: Math.max(1, Math.round(effect.duration)),
    ...(effect.kind === "slide" ? { distance: effect.distance ?? DEFAULT_SLIDE_DISTANCE } : {}),
  });
  const out = [...rest];
  out.splice(index >= 0 ? index : phase === "entrance" ? 0 : out.length, 0, track);
  return out;
}

export function setEffect(
  doc: CreativeDocument,
  layoutId: string,
  elementId: string,
  phase: EffectPhase,
  effect: Effect | null
): Ops {
  return writeTracks(doc, layoutId, elementId, withEffect(tracksOf(doc, layoutId, elementId), phase, effect));
}

export const presetDistance = (track: AnimationTrack) => {
  const d = track.preset?.params?.distance;
  return typeof d === "number" ? d : 16;
};

// ─── Track and keyframe edits (pure on a track list) ───────────────────────

const PROVENANCE_KEYS = ["keyframes", "easing", "direction"] as const;

/** Patches a track; editing its keyframes/easing/direction by hand drops the preset provenance (honest labels). */
export function patchTrack(track: AnimationTrack, patch: Partial<Omit<AnimationTrack, "id">>): AnimationTrack {
  const next = { ...clone(track), ...clone(patch) } as AnimationTrack & { preset?: unknown };
  const handEdited = PROVENANCE_KEYS.some((k) => k in patch && JSON.stringify(patch[k]) !== JSON.stringify(track[k]));
  if (handEdited) delete next.preset;
  return next;
}

export const replaceTrack = (tracks: readonly AnimationTrack[], track: AnimationTrack) =>
  tracks.map((t) => (t.id === track.id ? track : t));

/** Moves a timeline track so that it starts at `atMs` (integer ms ≥ 0). */
export const moveTrack = (track: AnimationTrack, atMs: number): AnimationTrack =>
  track.start.mode === "timeline"
    ? patchTrack(track, { start: { mode: "timeline", at: Math.max(0, Math.round(atMs)) } })
    : track;

export const resizeTrack = (track: AnimationTrack, durationMs: number): AnimationTrack =>
  patchTrack(track, { duration: Math.max(1, Math.round(durationMs)) });

const roundAt = (v: number) => Math.round(v * 10_000) / 10_000;

/** Decimals the Creative Document accepts per animatable property (px/degrees: 2; scale/opacity: 4). */
const PRECISION: Readonly<Record<keyof AnimatableProps, number>> = {
  offsetX: 2,
  offsetY: 2,
  rotation: 2,
  scaleX: 4,
  scaleY: 4,
  opacity: 4,
};
export function toSchemaPrecision(props: Partial<Record<keyof AnimatableProps, number>>): AnimatableProps {
  return Object.fromEntries(
    Object.entries(props).map(([k, v]) => {
      const f = 10 ** PRECISION[k as keyof AnimatableProps];
      return [k, Math.round(v * f) / f];
    })
  );
}

/**
 * Inserts a keyframe at progress `at` carrying the values the track has THERE
 * (rounded to the document's precision), so nothing jumps at that instant.
 * With a non-linear easing the curve of the two new segments differs from the
 * single old one — as in any keyframe tool.
 */
export function addKeyframe(track: AnimationTrack, at: number): { track: AnimationTrack; index: number } {
  const p = roundAt(Math.min(1, Math.max(0, at)));
  const existing = track.keyframes.findIndex((k) => k.at === p);
  if (existing >= 0) return { track, index: existing };
  const props = toSchemaPrecision(sampleKeyframes(track, p));
  const keyframes = [...track.keyframes, { at: p, props }].sort((a, b) => a.at - b.at);
  return { track: patchTrack(track, { keyframes }), index: keyframes.findIndex((k) => k.at === p) };
}

export function removeKeyframe(track: AnimationTrack, index: number): AnimationTrack {
  return patchTrack(track, { keyframes: track.keyframes.filter((_, i) => i !== index) });
}

export type KeyframePatch = {
  readonly at?: number;
  readonly props?: Partial<Record<keyof AnimatableProps, number | null>>;
  readonly easing?: Easing | null;
};

/** Edits one keyframe: values (null removes a property from it), position, easing of the segment it starts. */
export function setKeyframe(track: AnimationTrack, index: number, patch: KeyframePatch): AnimationTrack {
  const keyframes = track.keyframes.map((k, i) => {
    if (i !== index) return clone(k);
    const removed = new Set(
      Object.entries(patch.props ?? {})
        .filter(([, v]) => v === null)
        .map(([key]) => key)
    );
    const props: Record<string, number> = Object.fromEntries([
      ...Object.entries(clone(k.props)).filter(([key]) => !removed.has(key)),
      ...Object.entries(patch.props ?? {}).filter((e): e is [string, number] => typeof e[1] === "number"),
    ]);
    const next: { at: number; props: AnimatableProps; easing?: Easing } = {
      at: patch.at !== undefined ? roundAt(patch.at) : k.at,
      props: props,
    };
    const easing = patch.easing === undefined ? k.easing : patch.easing;
    if (easing !== null && easing !== undefined) next.easing = easing;
    return next;
  });
  return patchTrack(track, { keyframes });
}

/** A new hand-made track at the playhead: two keyframes at rest, ready to edit. */
export function newCustomTrack(tracks: readonly AnimationTrack[], atMs: number, durationMs = 1_000): AnimationTrack {
  return {
    id: nextId(
      tracks.map((t) => t.id),
      "anim"
    ),
    phase: "custom",
    start: { mode: "timeline", at: Math.max(0, Math.round(atMs)) },
    duration: durationMs,
    easing: "easeInOut",
    keyframes: [
      { at: 0, props: { opacity: 1, offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1, rotation: 0 } },
      { at: 1, props: { opacity: 1, offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1, rotation: 0 } },
    ],
  };
}

export const newEmphasisTrack = (tracks: readonly AnimationTrack[], atMs: number): AnimationTrack =>
  expandPreset({
    preset: "pulse",
    trackId: nextId(
      tracks.map((t) => t.id),
      "pulse"
    ),
    at: Math.max(0, Math.round(atMs)),
    duration: 600,
    iterations: 2,
  });

/**
 * Gameplay feedback (7.6F): a "shake" or "pop" that starts when an interaction asks for it
 * (manual track, e.g. "Cuando en el juego hay un error → reproducir «Sacudir»").
 */
export const newFeedbackTrack = (tracks: readonly AnimationTrack[], preset: "shake" | "pop"): AnimationTrack => ({
  ...expandPreset({
    preset,
    trackId: nextId(
      tracks.map((t) => t.id),
      preset
    ),
    at: 0,
    duration: preset === "shake" ? 400 : 350,
    distance: 6,
  }),
  start: { mode: "manual" },
});

/** Visibility window of an element, shared by every format (null = whole scene). */
export function setTiming(
  doc: CreativeDocument,
  elementId: string,
  timing: { readonly start?: number; readonly end?: number } | null
): Ops {
  if (!findElement(doc, elementId)) return [];
  const clean = Object.fromEntries(Object.entries(timing ?? {}).map(([k, v]) => [k, Math.max(0, Math.round(v))]));
  return Object.keys(clean).length === 0
    ? [{ op: "updateElement", id: elementId, set: {}, unset: ["timing"] }]
    : [{ op: "updateElement", id: elementId, set: { timing: clean } }];
}

export const PRESET_IDS = Object.keys(ANIMATION_PRESETS_V1) as AnimationPresetId[];
